import React, { useState } from 'react';
import { NewsArticle, User } from '../types';
import { Newspaper, Eye, Calendar, User as UserIcon, X, Sparkles, CheckCircle, CreditCard } from 'lucide-react';
import { api } from '../api';

interface NewsSectionProps {
  newsList: NewsArticle[];
  user: User | null;
  onJoinMembership: () => void;
}

export const NewsSection: React.FC<NewsSectionProps> = ({ newsList, user, onJoinMembership }) => {
  const [category, setCategory] = useState('全部');
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const categories = ['全部', '协会动态', '水上科普', '会员风采', '赛事通知'];
  const filtered = newsList.filter((n) => category === '全部' || n.category === category);

  const openDetail = async (a: NewsArticle) => {
    setDetailLoading(true);
    setArticle(a);
    try {
      const full = await api<NewsArticle>('get_news_detail', { id: a.id });
      setArticle(full);
    } catch { /* 保留列表数据 */ }
    setDetailLoading(false);
  };

  return (
    <section className="py-12 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

        {/* 会员入会横幅 */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-sky-900 via-blue-900 to-indigo-950 p-6 sm:p-10 text-white shadow-2xl">
          <div className="absolute -right-10 -bottom-10 w-80 h-80 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-sky-500/20 text-sky-200 rounded-full text-xs font-bold border border-sky-400/30">
                <Sparkles className="w-3.5 h-3.5 text-sky-300" /><span>长顺县游泳协会 会员权益中心</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight">加入长顺县游泳协会 · 共享专属泳场特惠与培训</h2>
              <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                正式会员享有全县指定场馆票价折扣、年度体能训练指导、优先参与赛事报名资格、免费获取协会防溺水急救手册与电子会员数字勋章。
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-sky-200 pt-1">
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-400" /> 优先赛事报名资格</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-400" /> 专属 SWOLF 训练指导</span>
                <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-400" /> 电子证书存档防伪</span>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 text-center shrink-0 space-y-3">
              <div className="text-xs text-slate-300">个人年度会费</div>
              <div className="text-3xl font-black text-sky-300">¥ {user?.settings?.member_fee || '150'}.00 <span className="text-xs text-slate-300 font-normal">/年</span></div>
              {user?.member_status === 'active' ? (
                <div className="px-6 py-3 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 font-black text-xs rounded-xl">
                  ✓ 已是有效会员（{user.membership_expiry}）
                </div>
              ) : (
                <button onClick={onJoinMembership}
                  className="w-full px-6 py-3 bg-gradient-to-r from-sky-400 to-blue-500 hover:from-sky-300 hover:to-blue-400 text-slate-950 font-black text-xs rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
                  <CreditCard className="w-4 h-4" /><span>微信一键在线缴费/续费</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 新闻列表 */}
        <div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-700 bg-sky-100/80 px-3 py-1 rounded-full mb-2">
                <Newspaper className="w-3.5 h-3.5 text-sky-600" /><span>协会动态与水上科普</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">会员动态</h2>
            </div>
            <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-xl border border-slate-200 overflow-x-auto">
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-all whitespace-nowrap ${category === c ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-600 hover:text-sky-600 hover:bg-slate-50'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filtered.map((n) => (
              <article key={n.id} onClick={() => openDetail(n)}
                className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-sky-300 hover:shadow-xl transition-all cursor-pointer">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-bold text-sky-700 bg-sky-100 px-2 py-0.5 rounded">{n.category}</span>
                  {n.is_top === 1 && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">置顶</span>}
                  <span className="ml-auto flex items-center gap-1 text-[11px] text-slate-400"><Calendar className="w-3 h-3" />{n.publish_date}</span>
                </div>
                <h3 className="text-base font-bold text-slate-900 group-hover:text-sky-600 transition-colors mb-2">{n.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-2">{n.summary}</p>
                <div className="flex items-center justify-between text-[11px] text-slate-400">
                  <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" />{n.author}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{n.reads_count} 阅读</span>
                </div>
              </article>
            ))}
            {filtered.length === 0 && <div className="col-span-full text-center py-16 text-slate-400 text-sm">暂无相关动态</div>}
          </div>
        </div>
      </div>

      {/* 文章详情弹窗 */}
      {article && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-3xl w-full p-6 sm:p-10 shadow-2xl relative my-8">
            <button onClick={() => setArticle(null)} className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold text-sky-700 bg-sky-100 px-2.5 py-1 rounded">{article.category}</span>
              <span className="text-xs text-slate-400 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{article.publish_date}</span>
              <span className="text-xs text-slate-400 flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" />{article.author}</span>
              <span className="ml-auto text-xs text-slate-400 flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{article.reads_count}</span>
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-5">{article.title}</h3>
            {article.cover_image && <img src={article.cover_image} alt="" className="w-full h-56 object-cover rounded-2xl mb-5" referrerPolicy="no-referrer" />}
            <div className="text-sm text-slate-600 leading-7 whitespace-pre-wrap">
              {detailLoading ? '加载中...' : (article.content || article.summary)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
